/**
 * 02 服务台首页 `/home`
 * -----------------------------------------------------------------------------
 * 版面（设计稿「学员端 · 服务台首页」）：
 *   问候 + 消息入口 → 状态主卡（渐变）→ 报名进度 → 其他报名单 →
 *   常用功能宫格 → 公告 → 底部浮动导航
 *
 * - 宫格 **8 项由接口下发**（`GET /api/h5/entrances`），前端不硬编码入口数组（C-17）
 * - 状态主卡的**步骤口径**按业务流程固定为「填写信息 / 身份核验 / 缴费 / 报名成功」：
 *   设计稿写的是「资料核验」，但核验前移是硬口径（核验在提交前完成），
 *   若照抄会在界面上造出一个不存在的「提交后等核验」状态。
 */

import { REGISTRATION_STATUS_LABEL, type RegistrationStatus } from '@enroll/shared';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useEnrollOptions,
  useEntrances,
  useEntryCapture,
  useInboxUnreadCount,
  useMyRegistrations,
  useSession,
} from '../api/hooks';
import { formatCentsText } from '../components/FormRenderer/logic';
import { EntranceIcon } from '../components/icons';
import {
  Card,
  EmptyHint,
  IconButton,
  LoadingBlock,
  NoticeBar,
  PageShell,
  ProgressRail,
  StatusTag,
} from '../components/layout';
import { FLOW_STEP_COUNT, FLOW_STEPS, flowProgress, materialStepText } from '../lib/flow';
import { lastRegistrationId, rememberRegistration } from '../store/recent';

interface RegistrationSummary {
  id: string;
  status: RegistrationStatus;
  batchName: string;
  enrollTypeName: string;
  registrationNo: string;
  payableCents: number;
  /** 派生的教材维度（C-22）：决定第 5 步「领取教材」是否点亮 */
  materialStatus?: string;
  /**
   * 报名人姓名。服务端 `RegistrationBriefView` 一直有下发，这里只是首页用到的
   * 精简视图漏了它 —— 问候称呼要用真名，因为 `snsapi_base` 拿不到微信昵称。
   */
  applicantName?: string | null;
}

/** 主卡标题：一句话说清「现在到哪一步、下一步做什么」 */
function cardTitleOf(status: RegistrationStatus): string {
  switch (status) {
    case 'DRAFT':
      return '报名还在填写中';
    case 'REJECTED':
      return '材料需要修改后重提';
    case 'PENDING_PAYMENT':
      return '报名资料已提交';
    case 'ENROLLED':
      return '报名成功，等待开课';
    case 'REFUNDED':
      return '该报名单已退费';
    case 'VOIDED':
      return '该报名单已作废';
    default:
      return REGISTRATION_STATUS_LABEL[status];
  }
}

function cardDescOf(registration: RegistrationSummary): string {
  switch (registration.status) {
    case 'DRAFT':
      return '继续填写并完成身份核验后即可提交';
    case 'REJECTED':
      return '请按驳回原因修改材料，重新提交后名额继续保留';
    case 'PENDING_PAYMENT':
      // 口径 R-07 / 附件6：逾期**不做自动作废**，转人工跟进 ——
      // 「释放名额」是空头承诺（名额不会释放），还会制造无谓的焦虑
      return registration.payableCents > 0
        ? `请在截止时间前完成转账 ${formatCentsText(registration.payableCents)}，逾期请尽快联系客服`
        : '请在截止时间前完成转账，逾期请尽快联系客服';
    case 'ENROLLED':
      return '教材将于 3 个工作日内寄出，物流与开课通知见消息中心';
    default:
      return `${registration.batchName} · ${registration.enrollTypeName}`;
  }
}

function ctaOf(status: RegistrationStatus): string {
  if (status === 'PENDING_PAYMENT') return '去缴费';
  if (status === 'DRAFT' || status === 'REJECTED') return '继续填写';
  return '查看详情';
}

/**
 * 进度条 hint 的后半句：**到了第 5 步就说教材状态**（待领取 / 已发货 / 已签收），
 * 那是此刻学员唯一关心的；还没到就照报名单状态说（待缴费 / 报名成功…）。
 */
function progressTextOf(registration: RegistrationSummary): string {
  return registration.status === 'ENROLLED'
    ? materialStepText(registration.materialStatus)
    : REGISTRATION_STATUS_LABEL[registration.status];
}

export default function Home() {
  useEntryCapture();
  const navigate = useNavigate();
  const session = useSession();
  const entrances = useEntrances();
  const unread = useInboxUnreadCount();
  const mine = useMyRegistrations();
  const enrollOptions = useEnrollOptions();
  const recentId = lastRegistrationId();
  const registrations = mine.data?.items ?? [];
  const recent: RegistrationSummary | undefined =
    registrations.find((item) => item.id === recentId) ?? registrations[0];

  useEffect(() => {
    if (recent) rememberRegistration(recent.id);
  }, [recent]);

  const items = entrances.data?.items ?? [];
  const gridItems = items.filter((item) => item.group === 'GRID' || item.group === 'SERVICE');
  const unreadCount = unread.data ?? 0;

  /**
   * 问候里的称呼：优先**报名人姓名**，其次会话昵称，最后兜底「同学」。
   *
   * 为什么不是微信昵称：当前授权用 `snsapi_base`，微信**只回 openid**、不返回昵称与头像
   * （见 `wechat-auth.ts` 的 `WechatSession`，只有 `openid` 一个字段），所以
   * `session.nickname` 通常就是空 —— 真实姓名在报名单里。
   * 要拿微信昵称头像必须改用 `snsapi_userinfo`，代价是每次授权弹确认框，
   * 破坏「点菜单一闪进入服务台」的无感体验；实名报名场景下显示真名也更合适。
   */
  const displayName = recent?.applicantName ?? session.data?.nickname ?? '同学';

  const openRegistration = (registration: RegistrationSummary) => {
    if (registration.status === 'DRAFT' || registration.status === 'REJECTED') {
      navigate(`/enroll/${registration.id}`);
      return;
    }
    if (registration.status === 'PENDING_PAYMENT') {
      navigate(`/payment/${registration.id}`);
      return;
    }
    navigate(`/registration/${registration.id}`);
  };

  return (
    <PageShell tabbar layout="home">
      <header className="home-header">
        <div className="home-header__greeting">
          <span className="home-header__hi">{greetingByHour()}</span>
          <span className="home-header__name">{displayName}</span>
        </div>
        <div className="home-header__actions">
          <IconButton label="消息中心" dot={unreadCount > 0} onClick={() => navigate('/inbox')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M6 9.5a6 6 0 0 1 12 0c0 3.2.7 5 1.6 6.2H4.4C5.3 14.5 6 12.7 6 9.5z"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M10 19a2 2 0 0 0 4 0"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
              />
            </svg>
          </IconButton>
        </div>
      </header>

      {mine.isPending ? <LoadingBlock text="正在加载报名进度" /> : null}

      {recent ? (
        <>
          <button
            type="button"
            className="status-card"
            onClick={() => openRegistration(recent)}
            aria-label={`${cardTitleOf(recent.status)}，${ctaOf(recent.status)}`}
          >
            <span className="status-card__top">
              <StatusTag status={recent.status} ghost />
              <span className="status-card__batch">
                {recent.batchName} · {recent.enrollTypeName}
              </span>
            </span>
            <span className="status-card__title">{cardTitleOf(recent.status)}</span>
            <span className="status-card__desc">{cardDescOf(recent)}</span>
            <span className="status-card__footer">
              <span className="status-card__no mono-num">报名单号 {recent.registrationNo}</span>
              <span className="status-card__cta">{ctaOf(recent.status)}</span>
            </span>
          </button>

          <Card
            title="报名进度"
            hint={`第 ${flowProgress(recent.status, recent.materialStatus).stepNo} / ${FLOW_STEP_COUNT} 步 · ${progressTextOf(recent)}`}
          >
            <ProgressRail
              steps={FLOW_STEPS}
              current={flowProgress(recent.status, recent.materialStatus).railCurrent}
            />
          </Card>
        </>
      ) : null}

      {!mine.isPending && registrations.length === 0 ? (
        <Card title="还没有报名记录" hint="从「报名办理」开始">
          <p className="card__desc">
            填写基本信息并完成身份核验后提交，系统会按批次与类型计算应缴金额。
          </p>
          <button
            type="button"
            className="btn btn--primary btn--block"
            onClick={() => navigate('/enroll/new')}
          >
            开始报名
          </button>
        </Card>
      ) : null}

      {/*
        首页只承载**一张主单**（与设计稿一致）。多单切换交给「我的报名」列表页 ——
        在首页再铺一层「其他报名单 + 全部记录」会把宫格挤出首屏，
        而宫格里的「我的报名」本就是这些记录的入口。
      */}

      <div className="section-title-row">
        <span className="section-title">常用功能</span>
      </div>

      {entrances.isPending ? (
        <LoadingBlock text="正在加载入口" />
      ) : gridItems.length === 0 ? (
        <EmptyHint
          title="当前没有可用入口"
          description="入口由后台配置下发，请联系客服确认批次是否开放"
        />
      ) : (
        <div className="entry-grid">
          {gridItems.map((item) => (
            <button
              key={item.code}
              type="button"
              className="entry"
              onClick={() => navigate(item.route)}
            >
              <span className="entry__icon">
                <EntranceIcon name={item.icon} />
              </span>
              <span className="entry__label">{item.name}</span>
            </button>
          ))}
        </div>
      )}

      <NoticeBar text={noticeText(enrollOptions.data?.items)} />
    </PageShell>
  );
}

/**
 * 首页公告：跟着**当前可报组合**说，不写死年份与截止日。
 *
 * 为什么不再硬编码：原实现写的是「2026 年秋季批次报名进行中，10 月 20 日截止」
 * 并以 `2026-10-20` 判断是否加「（已截止）」。过了那天之后，公告变成
 * 「报名进行中…（已截止）」—— 一句话自相矛盾，且批次实际由后台配置、
 * 逐年更换，写死的日期和年份每换一次批次就假一次。
 *
 * 批次名来自 `/api/h5/enroll-options`（与「报名办理」页同一个 queryKey，
 * staleTime 60s，不额外发请求）：后台把批次改成 2027 春，这里就跟着变。
 * 空列表是**正常业务分支**（当前没有开放批次），给一句明确说明而不是空白。
 *
 * 一期公告随批次走、无后台配置入口（避免为此开契约口子）；后台可配置公告
 * 待 SysConfig 扩展时一并迁走。
 */
function noticeText(options: readonly { batchName: string }[] | undefined): string {
  // 还没取到（首屏加载中）：用不带批次信息的通用表述，先不说「暂无」——
  // 「暂无」是一个结论，不能在结果没回来之前就替服务端下。
  if (!options) return '报名进行中，请及时提交并完成缴费';
  if (options.length === 0) return '当前暂无可报批次，请留意公众号通知';
  const batchNames = [...new Set(options.map((item) => item.batchName))];
  return `${batchNames.join(' / ')} 报名进行中，请及时提交并完成缴费`;
}

/** 问候语按本地时段切换（设计稿为「下午好」） */
function greetingByHour(): string {
  const hour = new Date().getHours();
  if (hour < 6) return '夜深了';
  if (hour < 11) return '上午好';
  if (hour < 13) return '中午好';
  if (hour < 18) return '下午好';
  return '晚上好';
}
